import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { updateQuota } from '../services/api';
import { QuotaRequest } from '../types';
import './QuotaScreen.css';

interface QuotaScreenProps {
  onBack: () => void;
}

const QuotaScreen: React.FC<QuotaScreenProps> = ({ onBack }) => {
  const { isAuthenticated } = useAuth();
  const [username, setUsername] = useState('');
  const [quota, setQuota] = useState('');
  const [esp32Whitelist, setEsp32Whitelist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAuthenticated || !username.trim() || !quota.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    const quotaNumber = parseInt(quota);
    if (isNaN(quotaNumber) || quotaNumber < 0) {
      setError('Quota must be a valid positive number');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');

      const request: QuotaRequest = {
        user_name: username.trim(),
        quota: quotaNumber,
        esp32_whitelist: esp32Whitelist,
      };

      const response = await updateQuota(request);

      if (response.data.status === 'success') {
        setMessage(`Quota updated successfully for ${username}`);
        setUsername('');
        setQuota('');
        setEsp32Whitelist(false);
      } else {
        setError(response.data.description || 'Failed to update quota');
      }
    } catch (err: any) {
      const errorMsg = typeof err.response?.data?.description === 'string'
        ? err.response.data.description
        : 'Failed to update quota';
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setMessage('');
    setError('');
  };

  return (
    <div className="quota-container">
      <header className="quota-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <h1>Quota Management</h1>
      </header>

      <div className="quota-content">
        <div className="quota-form-card">
          <h2>Update User Quota</h2>
          <p className="form-description">
            Update the quota limit for a specific user and manage ESP32 whitelist access.
          </p>

          <form onSubmit={handleSubmit} className="quota-form">
            <div className="form-group">
              <label htmlFor="username">Username *</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  clearMessages();
                }}
                placeholder="Enter username or email"
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="quota">Quota Limit *</label>
              <input
                id="quota"
                type="number"
                value={quota}
                onChange={(e) => {
                  setQuota(e.target.value);
                  clearMessages();
                }}
                placeholder="Enter quota limit"
                min="0"
                disabled={loading}
                required
              />
              <small className="form-help">
                Specify the maximum number of allowed operations or resources
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={esp32Whitelist}
                  onChange={(e) => {
                    setEsp32Whitelist(e.target.checked);
                    clearMessages();
                  }}
                  disabled={loading}
                />
                <span className="checkbox-text">Add to ESP32 Whitelist</span>
              </label>
              <small className="form-help">
                Enable special ESP32 device access privileges for this user
              </small>
            </div>

            {error && <div className="error-message">{error}</div>}
            {message && <div className="success-message">{message}</div>}

            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Quota'}
            </button>
          </form>
        </div>

        <div className="quota-info-card">
          <h3>Quota Management Info</h3>
          <div className="info-section">
            <h4>What is Quota?</h4>
            <p>
              Quota defines the maximum number of operations or resources a user can consume.
              This helps manage system resources and ensures fair usage across all users.
            </p>
          </div>

          <div className="info-section">
            <h4>ESP32 Whitelist</h4>
            <p>
              Users on the ESP32 whitelist have special privileges to access ESP32-specific
              features and bypass certain limitations. Use this carefully as it grants
              elevated permissions.
            </p>
          </div>

          <div className="info-section">
            <h4>Best Practices</h4>
            <ul>
              <li>Start with conservative quota limits and increase as needed</li>
              <li>Monitor user usage patterns before adjusting quotas</li>
              <li>Document quota changes for audit purposes</li>
              <li>Only add trusted users to the ESP32 whitelist</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotaScreen;